import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, X, ArrowRight, BookOpen, Calendar, Utensils, ShoppingCart, Droplets, ChevronDown, ShieldCheck, Clock } from 'lucide-react';
// Reusable animation wrapper
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const PrimaryButton = ({
  children,
  onClick,
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      import('react-facebook-pixel').then(mod => {
        const ReactPixel = mod.default;
        ReactPixel.track('AddToCart');
      });
      window.open('https://www.puntovital.lat/cart/45428226818150:1', '_blank');
    }
  };
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={handleClick} className={`relative group overflow-hidden bg-gold-500 hover:bg-gold-600 text-white font-bold text-lg md:text-xl py-4 px-8 rounded-full shadow-lg transition-all duration-300 w-full md:w-auto ${className}`} data-id="element-1">
      <div className="absolute inset-0 w-full h-full bg-white/20 group-hover:animate-[shimmer_1.5s_infinite] -translate-x-full group-hover:translate-x-full transition-transform skew-x-12" data-id="element-2"></div>
      <span className="relative flex items-center justify-center gap-2" data-id="element-3">
        {children}
        <ArrowRight className="w-6 h-6" data-id="element-4" />
      </span>
    </motion.button>;
};
export function App() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream-50 selection:bg-forest-800 selection:text-white" data-id="element-5">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 px-6 overflow-hidden bg-cream-100" data-id="element-6">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-7">
          <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-forest-800/5 blur-3xl" data-id="element-8"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-gold-500/10 blur-3xl" data-id="element-9"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-10">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-11">
            <FadeIn data-id="element-12">
              <div className="inline-block mb-4 px-4 py-1.5 bg-forest-800/10 text-forest-900 rounded-full text-sm font-semibold tracking-wide uppercase" data-id="element-13">
                Nuevo Lanzamiento
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-14">
                Protocolo Adiós Glucosa Incógnita:{' '}
                <span className="text-forest-800 italic" data-id="element-15">
                  El Método de 7 Días para Desinflamar tu Abdomen
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-16">
                Recupera tu energía y deja de pelear con el cierre de tu
                pantalón sin dejar de comer lo que amas (ni gastar en dietas
                imposibles).
              </p>
              <div className="animate-pulse-slow" data-id="element-17">
                <PrimaryButton onClick={scrollToOffer} data-id="element-18">
                  ¡QUIERO DESINFLAMARME AHORA!
                </PrimaryButton>
              </div>
              <div className="mt-6 flex items-center gap-4 text-sm text-gray-600 font-medium" data-id="element-19">
                <div className="flex -space-x-2" data-id="element-20">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-300 border-2 border-cream-100 flex items-center justify-center overflow-hidden" data-id="element-21">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-22" />
                    </div>)}
                </div>
                <p data-id="element-23">+2,500 mujeres ya lo probaron</p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="relative" data-id="element-24">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/50" data-id="element-25">
                <img src="https://cdn.magicpatterns.com/uploads/9BAHzn6bZatk6jPkfLupzN/heylloyd-image-1775614614072.png" alt="Protocolo Adiós Glucosa Incógnita en dispositivos" className="w-full h-auto object-cover" data-id="element-26" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-27"></div>
              </div>
              {/* Floating badge */}
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center gap-3" data-id="element-28">
                <div className="bg-green-100 p-2 rounded-full" data-id="element-29">
                  <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <p className="text-xs text-gray-500 font-medium" data-id="element-32">
                    Resultados en
                  </p>
                  <p className="font-bold text-forest-900" data-id="element-33">Solo 7 Días</p>
                </div>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* OPENING HOOK / PROBLEM SECTION */}
      <section className="py-24 px-6 bg-white" data-id="element-34">
        <div className="max-w-3xl mx-auto" data-id="element-35">
          <FadeIn data-id="element-36">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 leading-tight" data-id="element-37">
              ¿Te despiertas sintiéndote "pesada", con la cara hinchada y esa
              molesta sensación de que tu ropa te aprieta más que la semana
              pasada?
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-38">
            <FadeIn delay={0.1} data-id="element-39">
              <p data-id="element-40">
                No es falta de voluntad y tampoco es que "estés subiendo de
                peso" de la noche a la mañana.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-41">
              <p className="font-medium text-forest-900 text-xl border-l-4 border-gold-500 pl-6 py-2 bg-cream-50" data-id="element-42">
                Lo que estás experimentando es un{' '}
                <span className="font-bold" data-id="element-43">secuestro metabólico</span>{' '}
                provocado por picos de glucosa invisibles que inflaman tu cuerpo
                cada vez que desayunas o almuerzas.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-44">
              <p data-id="element-45">
                Pasas el día en una montaña rusa de energía. Empiezas con ganas,
                pero a las 4 de la tarde te golpea un cansancio extremo que solo
                se quita con algo dulce o un café con mucha azúcar.
              </p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-46">
              <p data-id="element-47">
                Has intentado jugos "detox" que solo te dan más hambre, has
                contado calorías hasta el cansancio y has eliminado el pan por
                completo, solo para terminar dándote un atracón el fin de semana
                por la ansiedad.
              </p>
            </FadeIn>
            <FadeIn delay={0.5} data-id="element-48">
              <p className="font-semibold text-dark" data-id="element-49">
                El problema no es lo que comes, es que tu cuerpo está atrapado
                en un ciclo de insulina que bloquea tu quema de grasa y te
                mantiene inflamada los 365 días del año.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 px-6 bg-forest-900 text-white relative overflow-hidden" data-id="element-50">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} data-id="element-51"></div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-52">
          <FadeIn data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-cream-50" data-id="element-54">
              Por qué lo que has intentado hasta ahora{' '}
              <span className="text-gold-500 italic" data-id="element-55">ha fallado</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-56">
            {['Las dietas restrictivas matan tu metabolismo y generan un efecto rebote inmediato.', 'Contar calorías es inútil si no controlas el orden en que esos nutrientes llegan a tu sangre.', 'Los jugos verdes comerciales suelen ser bombas de fructosa que disparan la inflamación en lugar de frenarla.', 'Ejercitarte por horas no sirve de nada si tus hormonas están ocupadas almacenando grasa debido al exceso de azúcar.'].map((text, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-57">
                <div className="bg-forest-800/50 border border-forest-800 p-6 rounded-2xl flex items-start gap-4 h-full hover:bg-forest-800 transition-colors" data-id="element-58">
                  <XCircle className="w-6 h-6 text-gold-500 shrink-0 mt-1" data-id="element-59" />
                  <p className="text-cream-100 leading-relaxed" data-id="element-60">{text}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 bg-cream-100" data-id="element-61">
        <div className="max-w-5xl mx-auto" data-id="element-62">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-63">
            <FadeIn data-id="element-64">
              <div className="aspect-square rounded-full bg-white shadow-2xl flex items-center justify-center p-12 relative" data-id="element-65">
                <div className="absolute inset-0 border-2 border-dashed border-gold-500 rounded-full animate-[spin_60s_linear_infinite]" data-id="element-66"></div>
                <div className="text-center" data-id="element-67">
                  <h3 className="text-2xl font-bold text-forest-900 mb-2" data-id="element-68">
                    Método V-P-C
                  </h3>
                  <p className="text-sm text-gray-500 uppercase tracking-widest mb-6" data-id="element-69">
                    El Orden Científico
                  </p>
                  <div className="flex flex-col gap-4" data-id="element-70">
                    <div className="bg-green-100 text-green-800 py-2 px-6 rounded-full font-medium" data-id="element-71">
                      1. Verdes (Fibra)
                    </div>
                    <div className="bg-red-100 text-red-800 py-2 px-6 rounded-full font-medium" data-id="element-72">
                      2. Proteína & Grasa
                    </div>
                    <div className="bg-amber-100 text-amber-800 py-2 px-6 rounded-full font-medium" data-id="element-73">
                      3. Carbohidratos
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            <div data-id="element-74">
              <FadeIn data-id="element-75">
                <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-76">
                  Presentamos el{' '}
                  <span className="text-forest-800" data-id="element-77">Muro de Fibra</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-78">
                  No se trata de qué comes, sino del{' '}
                  <span className="font-semibold text-forest-900" data-id="element-79">
                    orden científico
                  </span>{' '}
                  en que lo haces.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-80">
                  Al ingerir primero una red de fibra específica, creas un
                  filtro biológico en tu intestino que impide que los
                  carbohidratos entren de golpe a tu sangre.
                </p>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mt-8" data-id="element-81">
                  <p className="text-lg font-medium text-forest-900 italic" data-id="element-82">
                    "El resultado: puedes comer tu tortilla o tu postre
                    favorito, pero tu cuerpo lo procesará sin inflamarse y sin
                    almacenar grasa abdominal."
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 px-6 bg-white" data-id="element-83">
        <div className="max-w-4xl mx-auto" data-id="element-84">
          <FadeIn data-id="element-85">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-86">
              Todo lo que incluye tu acceso hoy
            </h2>
          </FadeIn>

          <div className="space-y-6" data-id="element-87">
            {[{
            icon: BookOpen,
            title: 'El Protocolo Maestro "Adiós Glucosa Incógnita"',
            desc: 'eBook PDF Paso a Paso con toda la ciencia explicada de forma simple.'
          }, {
            icon: Calendar,
            title: 'El Plan de Reseteo Metabólico de 7 Días',
            desc: 'Menú estructurado para que no tengas que pensar qué cocinar.'
          }, {
            icon: Utensils,
            title: 'La Guía de Bolsillo',
            desc: 'Qué pedir en restaurantes mexicanos para no inflamar.'
          }, {
            icon: ShoppingCart,
            title: 'Checklist: Lista de compras "Anti-Picos"',
            desc: 'Para el súper (Walmart / Soriana / Costco).'
          }, {
            icon: Droplets,
            title: 'Bono Especial: El Hack del Vinagre',
            desc: 'La ciencia de los 10 minutos antes de comer para reducir picos hasta un 30%.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-88">
                <div className="flex items-start gap-6 p-6 bg-cream-50 rounded-2xl border border-cream-100 hover:shadow-md transition-shadow" data-id="element-89">
                  <div className="bg-forest-900 p-4 rounded-xl text-gold-500 shrink-0" data-id="element-90">
                    <item.icon className="w-8 h-8" data-id="element-91" />
                  </div>
                  <div data-id="element-92">
                    <h3 className="text-xl font-bold text-forest-900 mb-2" data-id="element-93">
                      {item.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-94">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-6 bg-cream-100" data-id="element-95">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-96">
          <FadeIn data-id="element-97">
            <div className="bg-white p-8 rounded-3xl shadow-lg border-t-8 border-forest-800 h-full" data-id="element-98">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-forest-900" data-id="element-99">
                <CheckCircle2 className="w-8 h-8 text-forest-800" data-id="element-100" />
                Esto SÍ es para ti si...
              </h3>
              <ul className="space-y-5" data-id="element-101">
                {['Mujeres que se sienten hinchadas y pesadas después de comer.', 'Personas con antojos incontrolables de pan o dulce por la tarde.', "Quienes sufren de 'neblina mental' y fatiga crónica durante el día.", 'Mujeres que quieren desinflamar el abdomen de forma rápida y natural.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-102">
                    <div className="mt-1 w-2 h-2 rounded-full bg-forest-800 shrink-0" data-id="element-103"></div>
                    <span className="text-gray-700" data-id="element-104">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-105">
            <div className="bg-white p-8 rounded-3xl shadow-lg border-t-8 border-gray-300 h-full opacity-90" data-id="element-106">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-gray-800" data-id="element-107">
                <XCircle className="w-8 h-8 text-gray-400" data-id="element-108" />
                Esto NO es para ti si...
              </h3>
              <ul className="space-y-5" data-id="element-109">
                {['Personas que buscan pastillas mágicas para bajar de peso sin cambiar un solo hábito.', 'Quienes no están dispuestos a seguir un orden simple al comer.', 'Atletas de alto rendimiento que ya tienen un control metabólico clínico.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-110">
                    <div className="mt-1 w-2 h-2 rounded-full bg-gray-400 shrink-0" data-id="element-111"></div>
                    <span className="text-gray-600" data-id="element-112">{text}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 bg-forest-900 text-white" data-id="element-113">
        <div className="max-w-5xl mx-auto" data-id="element-114">
          <FadeIn data-id="element-115">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-cream-50" data-id="element-116">
              ¿Cómo funciona?{' '}
              <span className="italic font-light" data-id="element-117">Tan simple como 1, 2, 3</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-118">
            {[{
            step: '01',
            title: 'Descarga el Protocolo',
            desc: 'Recibe tu acceso inmediato a todo el material digital tras la compra.'
          }, {
            step: '02',
            title: 'Aplica el Orden Sagrado',
            desc: 'Cambia el orden de tus bocados en tu próxima comida (Verdes → Proteína → Carbohidratos).'
          }, {
            step: '03',
            title: 'Desinflama en 7 Días',
            desc: 'Nota cómo tu energía se estabiliza y tu abdomen se deshincha desde la primera mañana.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-119">
                <div className="relative p-8 rounded-3xl bg-forest-800/40 border border-forest-800 hover:bg-forest-800/60 transition-colors h-full" data-id="element-120">
                  <div className="text-6xl font-serif font-bold text-forest-800/50 absolute top-4 right-6" data-id="element-121">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gold-500 mb-4 relative z-10 mt-8" data-id="element-122">
                    {item.title}
                  </h3>
                  <p className="text-cream-100 relative z-10" data-id="element-123">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-24 px-6 bg-white" data-id="element-124">
        <div className="max-w-3xl mx-auto text-center" data-id="element-125">
          <FadeIn data-id="element-126">
            <ShieldCheck className="w-16 h-16 text-gold-500 mx-auto mb-6" data-id="element-127" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-128">
              La ciencia de la desinflamación rápida
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8" data-id="element-129">
              En solo 7 días, tu cuerpo dejará de retener líquidos innecesarios.{' '}
              <span className="font-bold text-forest-900" data-id="element-130">
                No es magia, es bioquímica aplicada:
              </span>
            </p>
            <div className="bg-cream-50 p-8 rounded-2xl border border-cream-100 text-left" data-id="element-131">
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-132">
                Al mantener tu glucosa estable, tus riñones dejan de retener
                sodio y la inflamación sistémica desaparece, permitiéndote
                recuperar tu figura natural sin pasar hambre un solo minuto.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-cream-100" data-id="element-133">
        <div className="max-w-3xl mx-auto" data-id="element-134">
          <FadeIn data-id="element-135">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-136">
              Preguntas Frecuentes
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-137">
            {[{
            q: '¿Tengo que dejar de comer tortillas?',
            a: 'No, aprenderás a comerlas al final para que no te inflamen.'
          }, {
            q: '¿Es difícil de seguir?',
            a: 'Es el método más fácil del mundo; solo cambias el orden de lo que ya tienes en el plato.'
          }, {
            q: '¿Necesito suplementos caros?',
            a: 'No, todo lo que necesitas está en el pasillo de verduras del súper.'
          }, {
            q: '¿En cuánto tiempo veo resultados?',
            a: 'La mayoría nota la cara y el abdomen menos hinchados a partir del tercer día.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-138">
                <details className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-id="element-139">
                  <summary className="flex items-center justify-between p-6 cursor-pointer font-bold text-lg text-forest-900" data-id="element-140">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" data-id="element-141" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-142">
                    {faq.a}
                  </div>
                </details>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section className="py-24 px-6 bg-forest-900 text-white relative overflow-hidden" data-id="element-143">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-144">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-full max-h-[800px] rounded-full bg-gold-500/10 blur-[100px]" data-id="element-145"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-146">
          <FadeIn data-id="element-147">
            <div className="bg-white text-dark rounded-3xl p-8 md:p-12 shadow-2xl text-center max-w-2xl mx-auto relative overflow-hidden" data-id="element-148">
              {/* Discount ribbon */}
              <div className="absolute -right-12 top-8 rotate-45 bg-red-500 text-white text-sm font-bold py-1.5 px-12 shadow-md z-20" data-id="element-149">
                -70% OFF
              </div>

              <h2 id="offer-section" className="text-3xl md:text-4xl font-bold mb-4 text-forest-900 scroll-mt-6" data-id="element-150">
                Comienza tu transformación hoy
              </h2>
              <p className="text-lg text-gray-600 mb-4" data-id="element-151">
                Obtén el protocolo completo y todos los bonos por un pago único
                de lanzamiento. Recupera tu bienestar hoy mismo por el precio de
                una comida corrida.
              </p>

              {/* Urgency badge */}
              <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 font-semibold text-sm px-4 py-2 rounded-full mb-8" data-id="element-152">
                <Clock className="w-4 h-4" data-id="element-153" />
                Oferta por tiempo limitado — ¡Últimas horas!
              </motion.div>

              <div className="mb-8" data-id="element-154">
                <p className="text-sm text-gray-400 uppercase tracking-widest font-bold mb-3" data-id="element-155">
                  Precio Especial de Lanzamiento
                </p>
                <div className="flex items-center justify-center gap-3 mb-2" data-id="element-156">
                  <span className="text-2xl md:text-3xl text-gray-400 line-through font-medium" data-id="element-157">
                    $997
                  </span>
                  <span className="text-sm text-gray-400 line-through mt-1" data-id="element-158">
                    MXN
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2" data-id="element-159">
                  <span className="text-5xl md:text-7xl font-bold text-forest-900" data-id="element-160">
                    $297
                  </span>
                  <span className="text-xl text-gray-500 font-medium mt-4" data-id="element-161">
                    MXN
                  </span>
                </div>
                <p className="text-green-600 font-semibold text-sm mt-3" data-id="element-162">
                  ¡Ahorras $700 MXN hoy!
                </p>
              </div>

              <PrimaryButton className="w-full py-5 text-xl mb-6" data-id="element-163">
                ¡QUIERO DESINFLAMARME AHORA!
              </PrimaryButton>

              <div className="flex flex-col items-center gap-3 text-sm text-gray-500" data-id="element-164">
                <div className="flex items-center gap-2" data-id="element-165">
                  <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-166" />
                  <span data-id="element-167">Acceso inmediato y seguro</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-168">
                  <Clock className="w-5 h-5 text-gold-500" data-id="element-169" />
                  <span data-id="element-170">Comienza tu transformación en 2 minutos</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-dark text-gray-400 py-8 text-center text-sm" data-id="element-171">
        <p data-id="element-172">
          © {new Date().getFullYear()} Protocolo Adiós Glucosa Incógnita. Todos
          los derechos reservados.
        </p>
        <p className="mt-2 text-xs max-w-2xl mx-auto px-6" data-id="element-173">
          Este producto no sustituye el consejo médico profesional. Consulta a
          tu médico antes de comenzar cualquier programa de salud.
        </p>
        <div className="mt-4 flex items-center justify-center gap-6" data-id="element-174">
          <button onClick={() => setLegalModal('privacy')} className="text-gray-500 hover:text-white transition-colors underline underline-offset-2" data-id="element-175">
            Política de Privacidad
          </button>
          <span className="text-gray-600" data-id="element-176">|</span>
          <button onClick={() => setLegalModal('terms')} className="text-gray-500 hover:text-white transition-colors underline underline-offset-2" data-id="element-177">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* LEGAL MODAL */}
      <AnimatePresence data-id="element-178">
        {legalModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setLegalModal(null)} data-id="element-179">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-180">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-181">
                <h3 className="text-xl font-bold text-forest-900" data-id="element-182">
                  {legalModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setLegalModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-183">
                  <X className="w-5 h-5 text-gray-500" data-id="element-184" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-185">
                {legalModal === 'privacy' ? <>
                    <p className="font-semibold text-base text-forest-900" data-id="element-186">
                      Última actualización: Abril 2026
                    </p>
                    <p data-id="element-187">
                      En Protocolo Adiós Glucosa Incógnita, respetamos tu
                      privacidad y nos comprometemos a proteger tus datos
                      personales conforme a la Ley Federal de Protección de
                      Datos Personales en Posesión de los Particulares
                      (LFPDPPP).
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-188">
                      1. Datos que recopilamos
                    </h4>
                    <p data-id="element-189">
                      Recopilamos únicamente los datos necesarios para procesar
                      tu compra: nombre, correo electrónico y datos de pago. No
                      almacenamos información de tarjetas de crédito
                      directamente; estos datos son procesados por plataformas
                      de pago seguras de terceros.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-190">
                      2. Uso de la información
                    </h4>
                    <p data-id="element-191">
                      Tus datos se utilizan exclusivamente para: entregar el
                      producto digital adquirido, enviar comunicaciones
                      relacionadas con tu compra y mejorar nuestros servicios.
                      No vendemos, alquilamos ni compartimos tu información
                      personal con terceros con fines comerciales.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-192">
                      3. Cookies
                    </h4>
                    <p data-id="element-193">
                      Nuestro sitio puede utilizar cookies para mejorar tu
                      experiencia de navegación y analizar el tráfico del sitio.
                      Puedes desactivar las cookies en la configuración de tu
                      navegador.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-194">
                      4. Seguridad
                    </h4>
                    <p data-id="element-195">
                      Implementamos medidas de seguridad técnicas y
                      organizativas para proteger tus datos contra acceso no
                      autorizado, pérdida o alteración.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-196">
                      5. Tus derechos ARCO
                    </h4>
                    <p data-id="element-197">
                      Tienes derecho a Acceder, Rectificar, Cancelar u Oponerte
                      al tratamiento de tus datos personales en cualquier
                      momento.
                    </p>
                  </> : <>
                    <p className="font-semibold text-base text-forest-900" data-id="element-198">
                      Última actualización: Abril 2026
                    </p>
                    <p data-id="element-199">
                      Al adquirir el Protocolo Adiós Glucosa Incógnita, aceptas
                      los siguientes términos y condiciones.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-200">
                      1. Naturaleza del producto
                    </h4>
                    <p data-id="element-201">
                      Este es un producto digital educativo (eBook y guías en
                      formato PDF). No constituye asesoría médica, diagnóstico
                      ni tratamiento. Siempre consulta a un profesional de salud
                      antes de realizar cambios en tu alimentación.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-202">
                      2. Entrega
                    </h4>
                    <p data-id="element-203">
                      El acceso al material digital se entrega de forma
                      inmediata tras confirmar el pago. Recibirás un enlace de
                      descarga en el correo electrónico proporcionado durante la
                      compra.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-204">
                      3. Política de reembolso
                    </h4>
                    <p data-id="element-205">
                      Debido a la naturaleza digital del producto, no se aceptan
                      devoluciones una vez que el material ha sido descargado.
                      Si tienes problemas con la descarga, contáctanos y te
                      ayudaremos a resolverlo.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-206">
                      4. Propiedad intelectual
                    </h4>
                    <p data-id="element-207">
                      Todo el contenido del protocolo está protegido por
                      derechos de autor. Queda prohibida su reproducción,
                      distribución o reventa sin autorización expresa.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-208">
                      5. Resultados
                    </h4>
                    <p data-id="element-209">
                      Los resultados pueden variar de persona a persona. Los
                      testimonios y ejemplos mencionados no garantizan
                      resultados específicos. Tu compromiso y constancia son
                      factores determinantes.
                    </p>
                    <h4 className="font-bold text-forest-900 mt-4" data-id="element-210">
                      6. Limitación de responsabilidad
                    </h4>
                    <p data-id="element-211">
                      No nos hacemos responsables por el uso inadecuado de la
                      información contenida en el protocolo ni por decisiones de
                      salud tomadas sin supervisión médica profesional.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}